import type {
  ClicktermInlineStyleIssue,
  ClicktermInlineStyleIssueReason,
  ClicktermInlineStyleOptions,
} from '../models';

export type InlineThemeSupports = (property: string, value: string) => boolean;

export interface InlineThemeNormalizationOptions {
  supports?: InlineThemeSupports;
}

export interface InlineThemeNormalizationResult {
  declarations: string[];
  issues: ClicktermInlineStyleIssue[];
}

type ThemeKind =
  | 'opaqueColor'
  | 'color'
  | 'background'
  | 'positiveLength'
  | 'nonNegLength'
  | 'offsetLength'
  | 'radius'
  | 'marginShorthand'
  | 'paddingShorthand'
  | 'borderWidthShorthand'
  | 'lineHeight'
  | 'fontWeight'
  | 'fontFamily'
  | 'shadow'
  | 'enum'
  | 'verticalAlign'
  | 'opacity'
  | 'positiveNumber';

interface ThemeRule {
  cssVar: string;
  kind: ThemeKind;
  cssProperty?: string;
  fallback: string;
}

interface NormalizedThemeValue {
  raw: unknown;
  value: string;
  emittedValue: string;
}

const schema: Record<string, ThemeRule> = {
  'checkbox.color': rule('--ct-w-inline-checkbox-accent', 'opaqueColor', 'color', 'var(--ct-w-primary-bg, #7f56d9)'),
  'checkbox.borderRadius': rule('--ct-w-inline-checkbox-radius', 'radius', 'border-radius', '4px'),
  'text.fontFamily': rule('--ct-w-inline-font-family', 'fontFamily', 'font-family', 'var(--ct-w-inline-theme-font-family-default)'),
  'checkbox.size': rule('--ct-w-inline-checkbox-size', 'positiveLength', 'width', '16px'),
  'text.fontSize': rule('--ct-w-inline-font-size', 'positiveLength', 'font-size', 'var(--ct-w-inline-theme-font-size-default)'),

  'checkbox.borderColor': rule('--ct-w-inline-checkbox-border-color', 'color', 'border-color', '#d5d7da'),
  'checkbox.background': rule('--ct-w-inline-checkbox-background', 'background', 'background', '#ffffff'),
  'checkbox.borderWidth': rule('--ct-w-inline-checkbox-border-width', 'borderWidthShorthand', 'border-width', '1px'),
  'checkbox.checkColor': rule('--ct-w-inline-checkbox-check-color', 'opaqueColor', 'stroke', 'var(--ct-w-inline-checkbox-accent, #7f56d9)'),
  'checkbox.checkWidth': rule('--ct-w-inline-checkbox-check-width', 'positiveNumber', 'stroke-width', '1.6'),
  'checkbox.boxShadow': rule('--ct-w-inline-checkbox-box-shadow', 'shadow', 'box-shadow', 'inset 0 1px 0 rgba(255, 255, 255, 0.4)'),
  'checkbox.topOffset': rule('--ct-w-inline-checkbox-top-offset', 'offsetLength', 'margin-top', '4px'),
  'checkbox.margin': rule('--ct-w-inline-checkbox-margin', 'marginShorthand', 'margin', '4px 0 0'),
  'checkbox.padding': rule('--ct-w-inline-checkbox-padding', 'paddingShorthand', 'padding', '0px'),
  'checkbox.verticalAlign': rule('--ct-w-inline-checkbox-align', 'verticalAlign', 'align-self', 'flex-start'),
  'checkbox.hover.borderColor': rule('--ct-w-inline-checkbox-hover-border-color', 'color', 'border-color', '#d5d7da'),
  'checkbox.hover.background': rule('--ct-w-inline-checkbox-hover-background', 'background', 'background', '#ffffff'),
  'checkbox.active.borderColor': rule('--ct-w-inline-checkbox-active-border-color', 'color', 'border-color', '#d5d7da'),
  'checkbox.active.background': rule('--ct-w-inline-checkbox-active-background', 'background', 'background', '#ffffff'),
  'checkbox.checked.background': rule('--ct-w-inline-checkbox-checked-background', 'background', 'background', '#f9f5ff'),
  'checkbox.checked.borderColor': rule('--ct-w-inline-checkbox-checked-border-color', 'color', 'border-color', 'var(--ct-w-inline-checkbox-accent, #7f56d9)'),
  'checkbox.checked.hover.background': rule('--ct-w-inline-checkbox-checked-hover-background', 'background', 'background', '#f9f5ff'),
  'checkbox.checked.hover.borderColor': rule('--ct-w-inline-checkbox-checked-hover-border-color', 'color', 'border-color', 'var(--ct-w-inline-checkbox-accent, #7f56d9)'),
  'checkbox.focus.outlineStyle': rule('--ct-w-inline-checkbox-focus-outline-style', 'enum', 'outline-style', 'solid'),
  'checkbox.focus.outlineColor': rule('--ct-w-inline-checkbox-focus-outline-color', 'color', 'outline-color', 'var(--ct-w-inline-checkbox-accent, #7f56d9)'),
  'checkbox.focus.outlineWidth': rule('--ct-w-inline-checkbox-focus-outline-width', 'nonNegLength', 'outline-width', '2px'),
  'checkbox.focus.outlineOffset': rule('--ct-w-inline-checkbox-focus-outline-offset', 'offsetLength', 'outline-offset', '2px'),
  'checkbox.focus.boxShadow': rule('--ct-w-inline-checkbox-focus-box-shadow', 'shadow', 'box-shadow', '0 0 0 2px var(--ct-w-inline-checkbox-accent, #7f56d9)'),
  'checkbox.disabled.background': rule('--ct-w-inline-checkbox-disabled-background', 'background', 'background', '#ffffff'),
  'checkbox.disabled.borderColor': rule('--ct-w-inline-checkbox-disabled-border-color', 'color', 'border-color', '#d5d7da'),
  'checkbox.disabled.opacity': rule('--ct-w-inline-checkbox-disabled-opacity', 'opacity', 'opacity', '0.75'),

  'text.color': rule('--ct-w-inline-text-color', 'opaqueColor', 'color', '#414651'),
  'text.lineHeight': rule('--ct-w-inline-line-height', 'lineHeight', 'line-height', 'var(--ct-w-inline-theme-line-height-default)'),
  'text.fontWeight': rule('--ct-w-inline-text-font-weight', 'fontWeight', 'font-weight', '400'),
  'text.letterSpacing': rule('--ct-w-inline-text-letter-spacing', 'offsetLength', 'letter-spacing', 'var(--ct-w-inline-text-letter-spacing-default)'),
  'text.textAlign': rule('--ct-w-inline-text-align', 'enum', 'text-align', 'var(--ct-w-inline-text-align-default)'),
  'text.contentSpacing': rule('--ct-w-inline-content-spacing', 'nonNegLength', 'margin-bottom', '14px'),
  'text.heading.color': rule('--ct-w-inline-heading-color', 'opaqueColor', 'color', '#181d27'),
  'text.heading.fontWeight': rule('--ct-w-inline-heading-font-weight', 'fontWeight', 'font-weight', '600'),
  'text.heading.fontFamily': rule('--ct-w-inline-heading-font-family', 'fontFamily', 'font-family', 'var(--ct-w-inline-font-family)'),
  'text.link.color': rule('--ct-w-inline-link-color', 'opaqueColor', 'color', '#7f56d9'),
  'text.link.textDecoration': rule('--ct-w-inline-link-decoration', 'enum', 'text-decoration', 'underline'),
  'text.link.fontWeight': rule('--ct-w-inline-link-font-weight', 'fontWeight', 'font-weight', 'var(--ct-w-inline-link-font-weight-default)'),
  'text.link.textUnderlineOffset': rule('--ct-w-inline-link-underline-offset', 'offsetLength', 'text-underline-offset', 'auto'),
  'text.link.textDecorationThickness': rule('--ct-w-inline-link-decoration-thickness', 'positiveLength', 'text-decoration-thickness', 'auto'),
  'text.link.hover.color': rule('--ct-w-inline-link-hover-color', 'opaqueColor', 'color', '#7f56d9'),
  'text.link.hover.textDecoration': rule('--ct-w-inline-link-hover-decoration', 'enum', 'text-decoration', 'underline'),
  'text.marks.bold.color': rule('--ct-w-inline-mark-bold-color', 'opaqueColor', 'color', 'currentColor'),
  'text.marks.bold.fontWeight': rule('--ct-w-inline-mark-bold-weight', 'fontWeight', 'font-weight', 'bold'),
  'text.marks.italic.color': rule('--ct-w-inline-mark-italic-color', 'opaqueColor', 'color', 'currentColor'),
  'text.marks.underline.color': rule('--ct-w-inline-mark-underline-color', 'opaqueColor', 'color', 'currentColor'),
  'text.marks.underline.textDecorationColor': rule('--ct-w-inline-mark-underline-line-color', 'opaqueColor', 'text-decoration-color', 'currentColor'),
  'text.marks.strikethrough.color': rule('--ct-w-inline-mark-strike-color', 'opaqueColor', 'color', 'currentColor'),
  'text.marks.strikethrough.textDecorationColor': rule('--ct-w-inline-mark-strike-line-color', 'opaqueColor', 'text-decoration-color', 'currentColor'),
  'text.marks.textColor': rule('--ct-w-inline-mark-color', 'opaqueColor', 'color', 'currentColor'),
  'text.marks.backgroundColor': rule('--ct-w-inline-mark-bg', 'background', 'background-color', 'transparent'),
  'text.marks.indentation.scale': rule('--ct-w-inline-indent-scale', 'positiveNumber', undefined, '1'),
  'text.marks.superscript.fontSize': rule('--ct-w-inline-sup-font-size', 'positiveLength', 'font-size', 'smaller'),
  'text.marks.subscript.fontSize': rule('--ct-w-inline-sub-font-size', 'positiveLength', 'font-size', 'smaller'),

  'container.background': rule('--ct-w-inline-container-background', 'background', 'background', '#ffffff'),
  'container.borderColor': rule('--ct-w-inline-container-border-color', 'color', 'border-color', '#e9eaeb'),
  'container.borderWidth': rule('--ct-w-inline-container-border-width', 'borderWidthShorthand', 'border-width', '1px'),
  'container.borderStyle': rule('--ct-w-inline-container-border-style', 'enum', 'border-style', 'solid'),
  'container.borderRadius': rule('--ct-w-inline-container-border-radius', 'radius', 'border-radius', '12px'),
  'container.boxShadow': rule('--ct-w-inline-container-box-shadow', 'shadow', 'box-shadow', '0 14px 30px rgba(24, 29, 39, 0.08)'),
  'container.padding': rule('--ct-w-inline-container-padding', 'paddingShorthand', 'padding', '18px'),
  'container.gap': rule('--ct-w-inline-container-gap', 'nonNegLength', 'gap', '14px'),
  'locked.background': rule('--ct-w-inline-locked-background', 'background', 'background', '#fafafa'),
  'locked.contentOpacity': rule('--ct-w-inline-locked-content-opacity', 'opacity', 'opacity', '0.9'),
};

const schemaPaths = Object.keys(schema);
const schemaPrefixes = new Set<string>();
schemaPaths.forEach(path => {
  const segments = path.split('.');
  for (let index = 1; index < segments.length; index += 1) {
    schemaPrefixes.add(segments.slice(0, index).join('.'));
  }
});

const reasonHints: Record<ClicktermInlineStyleIssueReason, string> = {
  'unknown-property': 'remove it or use a documented theme field',
  'invalid-type': 'use the documented value type',
  'invalid-value': 'use a finite value in the documented range',
  'unsafe-value': 'remove CSS injection or URL syntax',
  'unsupported-value': 'use a value supported for this CSS property',
  'focus-cue-required': 'keep a visible outline or provide focus.boxShadow',
  'checkbox-visibility-required': 'keep a visible border or non-transparent background',
  'link-cue-required': 'set a distinct color before removing the underline',
};

function rule(
  cssVar: string,
  kind: ThemeKind,
  cssProperty: string | undefined,
  fallback: string,
): ThemeRule {
  return { cssVar, kind, cssProperty, fallback };
}

function getDefaultSupports(): InlineThemeSupports | undefined {
  const css = typeof globalThis === 'object' ? globalThis.CSS : undefined;
  return typeof css?.supports === 'function' ? css.supports.bind(css) : undefined;
}

export function sanitizeCssValue(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || /[;{}]/.test(trimmed)) {
    return null;
  }
  const normalized = trimmed.toLowerCase();
  if (
    normalized.includes('javascript:')
    || normalized.includes('expression(')
    || normalized.includes('url(')
  ) {
    return null;
  }
  return trimmed;
}

export function normalizeInlineTheme(
  style: ClicktermInlineStyleOptions | unknown,
  options: InlineThemeNormalizationOptions = {},
): InlineThemeNormalizationResult {
  const issues: ClicktermInlineStyleIssue[] = [];
  const rawValues = new Map<string, unknown>();
  collectRawValues(style, '', rawValues, issues);

  const supports = options.supports ?? getDefaultSupports();
  const normalizedValues = new Map<string, NormalizedThemeValue>();

  schemaPaths.forEach(path => {
    if (!rawValues.has(path)) {
      return;
    }
    const raw = rawValues.get(path);
    const normalized = normalizeThemeValue(schema[path], raw, supports);
    if ('reason' in normalized) {
      issues.push({ path, value: raw, reason: normalized.reason });
      return;
    }
    normalizedValues.set(path, {
      raw,
      value: normalized.value,
      emittedValue: hardenBareVar(normalized.value, schema[path].fallback),
    });
  });

  applyFocusCueGuard(normalizedValues, issues);
  applyCheckboxVisibilityGuards(normalizedValues, issues);
  applyLinkCueGuards(normalizedValues, issues);

  const declarations: string[] = [];
  schemaPaths.forEach(path => {
    const normalized = normalizedValues.get(path);
    if (!normalized) {
      return;
    }
    declarations.push(`${schema[path].cssVar}: ${normalized.emittedValue}`);
    if (path === 'text.fontSize' && !normalizedValues.has('text.lineHeight')) {
      declarations.push(
        '--ct-w-inline-line-height: calc(var(--ct-w-inline-type-body-line-height) / var(--ct-w-inline-type-body-size))'
      );
    }
  });

  return { declarations, issues };
}

function collectRawValues(
  value: unknown,
  path: string,
  output: Map<string, unknown>,
  issues: ClicktermInlineStyleIssue[],
): void {
  if (!isRecord(value)) {
    if (path || value !== undefined) {
      issues.push({ path: path || 'style', value, reason: 'invalid-type' });
    }
    return;
  }

  Object.keys(value).forEach(key => {
    const childPath = path ? `${path}.${key}` : key;
    const childValue = value[key];
    if (schema[childPath]) {
      output.set(childPath, childValue);
      return;
    }
    if (schemaPrefixes.has(childPath)) {
      collectRawValues(childValue, childPath, output, issues);
      return;
    }
    issues.push({ path: childPath, value: childValue, reason: 'unknown-property' });
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeThemeValue(
  themeRule: ThemeRule,
  raw: unknown,
  supports: InlineThemeSupports | undefined,
): { value: string } | { reason: ClicktermInlineStyleIssueReason } {
  if (themeRule.kind === 'opacity') {
    return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 && raw <= 1
      ? { value: String(raw) }
      : { reason: typeof raw === 'number' ? 'invalid-value' : 'invalid-type' };
  }

  if (themeRule.kind === 'positiveNumber') {
    if (typeof raw !== 'number') {
      return { reason: 'invalid-type' };
    }
    return Number.isFinite(raw) && raw > 0
      ? validateSupports(themeRule, String(raw), supports)
      : { reason: 'invalid-value' };
  }

  if (themeRule.kind === 'fontWeight' && typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < 1 || raw > 1000) {
      return { reason: 'invalid-value' };
    }
    return validateSupports(themeRule, String(raw), supports);
  }

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      return { reason: 'invalid-value' };
    }
    if (!isDimensionKind(themeRule.kind)) {
      return { reason: 'invalid-type' };
    }
    if (requiresPositive(themeRule.kind) && raw <= 0) {
      return { reason: 'invalid-value' };
    }
    if (requiresNonNegative(themeRule.kind) && raw < 0) {
      return { reason: 'invalid-value' };
    }
    return validateSupports(themeRule, `${raw}px`, supports);
  }

  if (typeof raw !== 'string') {
    return { reason: 'invalid-type' };
  }

  const value = sanitizeCssValue(raw);
  if (!value) {
    return { reason: 'unsafe-value' };
  }

  if (themeRule.kind === 'verticalAlign') {
    const alignment = {
      top: 'flex-start',
      middle: 'center',
      center: 'center',
      bottom: 'flex-end',
    }[value];
    if (!alignment) {
      return { reason: 'unsupported-value' };
    }
    return validateSupports(themeRule, alignment, supports);
  }

  if (themeRule.kind === 'opaqueColor' && isExplicitlyTransparent(value)) {
    return { reason: 'invalid-value' };
  }

  if (themeRule.kind === 'fontWeight') {
    const numericWeight = parsePlainNumber(value);
    if (numericWeight !== null && (numericWeight < 1 || numericWeight > 1000)) {
      return { reason: 'invalid-value' };
    }
  }

  if (isDimensionKind(themeRule.kind)) {
    const literalValues = splitCssValueList(value);
    if (
      (themeRule.kind === 'marginShorthand'
        || themeRule.kind === 'paddingShorthand'
        || themeRule.kind === 'borderWidthShorthand'
        || themeRule.kind === 'radius')
      && (literalValues.length < 1 || literalValues.length > 4)
      && !value.includes('/')
    ) {
      return { reason: 'invalid-value' };
    }
    const numericValues = literalValues.map(parseSingleCssNumber).filter((item): item is number => item !== null);
    if (numericValues.length === literalValues.length) {
      if (requiresPositive(themeRule.kind) && numericValues.some(number => number <= 0)) {
        return { reason: 'invalid-value' };
      }
      if (requiresNonNegative(themeRule.kind) && numericValues.some(number => number < 0)) {
        return { reason: 'invalid-value' };
      }
    }
  }

  return validateSupports(themeRule, value, supports);
}

function validateSupports(
  themeRule: ThemeRule,
  value: string,
  supports: InlineThemeSupports | undefined,
): { value: string } | { reason: ClicktermInlineStyleIssueReason } {
  if (themeRule.cssProperty && supports && !supports(themeRule.cssProperty, value)) {
    return { reason: 'unsupported-value' };
  }
  return { value };
}

function isDimensionKind(kind: ThemeKind): boolean {
  return [
    'positiveLength',
    'nonNegLength',
    'offsetLength',
    'radius',
    'marginShorthand',
    'paddingShorthand',
    'borderWidthShorthand',
    'lineHeight',
  ].includes(kind);
}

function requiresPositive(kind: ThemeKind): boolean {
  return kind === 'positiveLength' || kind === 'lineHeight';
}

function requiresNonNegative(kind: ThemeKind): boolean {
  return kind === 'nonNegLength'
    || kind === 'radius'
    || kind === 'paddingShorthand'
    || kind === 'borderWidthShorthand';
}

function splitCssValueList(value: string): string[] {
  const values: string[] = [];
  let current = '';
  let depth = 0;
  for (const character of value.trim()) {
    if (character === '(') depth += 1;
    if (character === ')') depth -= 1;
    if (/\s/.test(character) && depth === 0) {
      if (current) {
        values.push(current);
        current = '';
      }
      continue;
    }
    current += character;
  }
  if (current) values.push(current);
  return values;
}

function parseSingleCssNumber(value: string): number | null {
  let candidate = value.trim();
  let wrapped = candidate.match(/^(calc|min|max|clamp)\(\s*([\s\S]*)\s*\)$/i);
  while (wrapped && hasBalancedParentheses(wrapped[2])) {
    const functionName = wrapped[1].toLowerCase();
    const argumentsList = splitTopLevel(wrapped[2], ',');
    if (functionName === 'calc' && argumentsList.length === 1) {
      candidate = argumentsList[0];
      wrapped = candidate.match(/^(calc|min|max|clamp)\(\s*([\s\S]*)\s*\)$/i);
      continue;
    }
    const resolved = argumentsList.map(parseSingleCssNumber);
    if (resolved.some(item => item === null)) {
      return null;
    }
    const signs = resolved.map(item => Math.sign(item!));
    if (functionName === 'min' && signs.length > 0) {
      return Math.min(...signs);
    }
    if (functionName === 'max' && signs.length > 0) {
      return Math.max(...signs);
    }
    if (functionName === 'clamp' && signs.length === 3) {
      const [minimum, preferred, maximum] = signs;
      if (minimum > 0 || (preferred > 0 && maximum > 0)) return 1;
      if (maximum < 0 || (preferred < 0 && minimum < 0)) return -1;
      if (preferred === 0 || (minimum === 0 && preferred < 0) || (maximum === 0 && preferred > 0)) {
        return 0;
      }
    }
    return null;
  }
  const match = candidate.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(?:[a-z%]+)?$/i);
  return match ? Number(match[1]) : parseSimpleCssSum(candidate);
}

function parseSimpleCssSum(value: string): number | null {
  const compact = value.replace(/\s+/g, '');
  const termPattern = /([+-]?)(\d+(?:\.\d+)?|\.\d+)([a-z%]*)/gi;
  const terms: Array<{ value: number; unit: string }> = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = termPattern.exec(compact))) {
    if (match.index !== cursor) return null;
    const sign = match[1] === '-' ? -1 : 1;
    terms.push({ value: sign * Number(match[2]), unit: match[3].toLowerCase() });
    cursor = termPattern.lastIndex;
  }
  if (!terms.length || cursor !== compact.length) {
    return null;
  }
  const nonZeroUnits = new Set(terms.filter(term => term.value !== 0).map(term => term.unit));
  if (nonZeroUnits.size > 1) {
    return null;
  }
  return terms.reduce((total, term) => total + term.value, 0);
}

function hasBalancedParentheses(value: string): boolean {
  let depth = 0;
  for (const character of value) {
    if (character === '(') depth += 1;
    if (character === ')') depth -= 1;
    if (depth < 0) return false;
  }
  return depth === 0;
}

function parsePlainNumber(value: string): number | null {
  return /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(value) ? Number(value) : null;
}

function hardenBareVar(value: string, fallback: string): string {
  const match = value.match(/^var\(\s*(--[a-z0-9_-]+)\s*\)$/i);
  return match ? `var(${match[1]}, ${fallback})` : value;
}

function isExplicitlyTransparent(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'transparent' || normalized === 'none') {
    return true;
  }
  if (/^#[0-9a-f]{4}$/.test(normalized)) {
    return normalized.endsWith('0');
  }
  if (/^#[0-9a-f]{8}$/.test(normalized)) {
    return normalized.endsWith('00');
  }
  if (/\/\s*(?:0(?:\.0+)?|0%)\s*\)$/.test(normalized)) {
    return true;
  }
  const functional = normalized.match(/^(?:rgba|hsla)\((.*)\)$/);
  if (!functional) {
    return false;
  }
  const parts = functional[1].split(',').map(part => part.trim());
  if (parts.length !== 4) {
    return false;
  }
  return /^(?:0(?:\.0+)?|0%)$/.test(parts[3]);
}

function applyFocusCueGuard(
  values: Map<string, NormalizedThemeValue>,
  issues: ClicktermInlineStyleIssue[],
): void {
  const disablingPaths = [
    'checkbox.focus.outlineStyle',
    'checkbox.focus.outlineColor',
    'checkbox.focus.outlineWidth',
  ].filter(path => {
    const value = values.get(path)?.value;
    if (!value) return false;
    if (path.endsWith('outlineStyle')) return value.toLowerCase() === 'none';
    if (path.endsWith('outlineColor')) return isExplicitlyTransparent(value);
    return isZeroWidth(value);
  });

  if (!disablingPaths.length) {
    return;
  }

  const focusShadow = values.get('checkbox.focus.boxShadow')?.value;
  const baseShadow = values.get('checkbox.boxShadow')?.value
    ?? 'inset 0 1px 0 rgba(255, 255, 255, 0.4)';
  if (focusShadow && isVisibleFocusShadow(focusShadow, baseShadow)) {
    return;
  }

  disablingPaths.forEach(path => rejectNormalizedValue(values, issues, path, 'focus-cue-required'));
  if (focusShadow) {
    rejectNormalizedValue(values, issues, 'checkbox.focus.boxShadow', 'focus-cue-required');
  }
}

function isVisibleFocusShadow(value: string, baseShadow: string): boolean {
  const normalized = normalizeComparableCss(value);
  if (normalized === 'none' || normalized === normalizeComparableCss(baseShadow)) {
    return false;
  }
  return splitTopLevel(value, ',').some(layer => (
    !containsExplicitlyTransparentColor(layer) && !hasZeroShadowGeometry(layer)
  ));
}

function normalizeComparableCss(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function splitTopLevel(value: string, separator: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  for (const character of value) {
    if (character === '(') depth += 1;
    if (character === ')') depth -= 1;
    if (character === separator && depth === 0) {
      if (current.trim()) parts.push(current.trim());
      current = '';
      continue;
    }
    current += character;
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

function containsExplicitlyTransparentColor(value: string): boolean {
  if (/\btransparent\b/i.test(value)) {
    return true;
  }
  const colors = value.match(/#[0-9a-f]{4}(?:[0-9a-f]{4})?\b|(?:rgba?|hsla?)\([^)]*\)/gi) ?? [];
  return colors.some(isExplicitlyTransparent);
}

function hasZeroShadowGeometry(value: string): boolean {
  const tokens = splitCssValueList(value).filter(token => token.toLowerCase() !== 'inset');
  const geometry: number[] = [];
  let hasUnknownGeometry = false;
  tokens.forEach(token => {
    const numeric = parseSingleCssNumber(token);
    if (numeric !== null) {
      geometry.push(numeric);
      return;
    }
    if (!isColorLikeToken(token)) {
      hasUnknownGeometry = true;
    }
  });
  return !hasUnknownGeometry && geometry.length >= 2 && geometry.every(number => number === 0);
}

function isColorLikeToken(value: string): boolean {
  return /^#[0-9a-f]+$/i.test(value)
    || /^[a-z]+$/i.test(value)
    || /^(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|color-mix)\(/i.test(value);
}

interface CheckboxStatePaths {
  background?: string;
  borderColor?: string;
}

function applyCheckboxVisibilityGuards(
  values: Map<string, NormalizedThemeValue>,
  issues: ClicktermInlineStyleIssue[],
): void {
  guardCheckboxState(values, issues, {
    background: 'checkbox.background',
    borderColor: 'checkbox.borderColor',
  }, 'base');

  const statePaths: Array<[CheckboxStatePaths, string]> = [
    [{ background: 'checkbox.hover.background', borderColor: 'checkbox.hover.borderColor' }, 'base'],
    [{ background: 'checkbox.active.background', borderColor: 'checkbox.active.borderColor' }, 'base'],
    [{ background: 'checkbox.checked.background', borderColor: 'checkbox.checked.borderColor' }, 'checked'],
    [{ background: 'checkbox.checked.hover.background', borderColor: 'checkbox.checked.hover.borderColor' }, 'checked-hover'],
    [{ background: 'checkbox.disabled.background', borderColor: 'checkbox.disabled.borderColor' }, 'base'],
  ];
  statePaths.forEach(([paths, state]) => guardCheckboxState(values, issues, paths, state));
}

function guardCheckboxState(
  values: Map<string, NormalizedThemeValue>,
  issues: ClicktermInlineStyleIssue[],
  paths: CheckboxStatePaths,
  state: string,
): void {
  const baseBackground = values.get('checkbox.background')?.value ?? '#ffffff';
  const baseBorder = values.get('checkbox.borderColor')?.value ?? '#d5d7da';
  const borderWidth = values.get('checkbox.borderWidth')?.value ?? '1px';
  const checkedBackground = values.get('checkbox.checked.background')?.value
    ?? values.get('checkbox.background')?.value
    ?? '#f9f5ff';
  const checkedBorder = values.get('checkbox.checked.borderColor')?.value
    ?? values.get('checkbox.color')?.value
    ?? '#7f56d9';

  const inheritedBackground = state === 'checked-hover'
    ? checkedBackground
    : state === 'checked'
      ? checkedBackground
      : baseBackground;
  const inheritedBorder = state === 'checked-hover'
    ? checkedBorder
    : state === 'checked'
      ? checkedBorder
      : baseBorder;
  const background = (paths.background && values.get(paths.background)?.value) ?? inheritedBackground;
  const borderColor = (paths.borderColor && values.get(paths.borderColor)?.value) ?? inheritedBorder;
  const borderVisible = !isZeroWidth(borderWidth) && !isExplicitlyTransparent(borderColor);
  const backgroundVisible = !isExplicitlyTransparent(background);

  if (borderVisible || backgroundVisible) {
    return;
  }

  if (paths.background && values.has(paths.background) && isExplicitlyTransparent(background)) {
    rejectNormalizedValue(values, issues, paths.background, 'checkbox-visibility-required');
  }
  if (paths.borderColor && values.has(paths.borderColor) && isExplicitlyTransparent(borderColor)) {
    rejectNormalizedValue(values, issues, paths.borderColor, 'checkbox-visibility-required');
  }
  if (state === 'base' && values.has('checkbox.borderWidth') && isZeroWidth(borderWidth)) {
    rejectNormalizedValue(values, issues, 'checkbox.borderWidth', 'checkbox-visibility-required');
  }
}

function isZeroWidth(value: string): boolean {
  const parts = splitCssValueList(value);
  return parts.length > 0 && parts.every(part => parseSingleCssNumber(part) === 0);
}

function applyLinkCueGuards(
  values: Map<string, NormalizedThemeValue>,
  issues: ClicktermInlineStyleIssue[],
): void {
  if (
    values.get('text.link.textDecoration')?.value.toLowerCase() === 'none'
    && !values.has('text.link.color')
  ) {
    rejectNormalizedValue(values, issues, 'text.link.textDecoration', 'link-cue-required');
  }
  if (
    values.get('text.link.hover.textDecoration')?.value.toLowerCase() === 'none'
    && !values.has('text.link.hover.color')
  ) {
    rejectNormalizedValue(values, issues, 'text.link.hover.textDecoration', 'link-cue-required');
  }
}

function rejectNormalizedValue(
  values: Map<string, NormalizedThemeValue>,
  issues: ClicktermInlineStyleIssue[],
  path: string,
  reason: ClicktermInlineStyleIssueReason,
): void {
  const normalized = values.get(path);
  if (!normalized) {
    return;
  }
  values.delete(path);
  issues.push({ path, value: normalized.raw, reason });
}

export interface ContentDeclarationRewriteOptions {
  tagName?: string;
  supports?: InlineThemeSupports;
}

export function rewriteContentDeclaration(
  property: string,
  value: string,
  options: ContentDeclarationRewriteOptions = {},
): string[] {
  const name = property.trim().toLowerCase();
  const originalValue = value.trim();
  const supports = options.supports ?? getDefaultSupports();
  if (
    !originalValue
    || (supports && !supports(name, originalValue))
  ) {
    return [`${name}: ${originalValue}`];
  }

  if (name === 'color') {
    const tagName = options.tagName?.toLowerCase();
    const colorVariables = tagName === 'a'
      ? ['--ct-w-inline-link-color', '--ct-w-inline-mark-color']
      : isHeadingTag(tagName)
        ? ['--ct-w-inline-heading-color', '--ct-w-inline-mark-color']
        : tagName === 'strong' || tagName === 'b'
          ? ['--ct-w-inline-mark-bold-color', '--ct-w-inline-mark-color']
          : tagName === 'em' || tagName === 'i'
            ? ['--ct-w-inline-mark-italic-color', '--ct-w-inline-mark-color']
            : tagName === 'u'
              ? ['--ct-w-inline-mark-underline-color', '--ct-w-inline-mark-color']
              : tagName === 's'
                ? ['--ct-w-inline-mark-strike-color', '--ct-w-inline-mark-color']
                : ['--ct-w-inline-mark-color'];
    const color = withThemeFallback(colorVariables, originalValue);
    return [`color: ${color}`];
  }

  if (name === 'background-color') {
    return [`background-color: var(--ct-w-inline-mark-bg, ${originalValue})`];
  }

  if (name === 'text-decoration') {
    const decoration = options.tagName?.toLowerCase() === 'a'
      ? withThemeFallback(['--ct-w-inline-link-decoration'], originalValue)
      : originalValue;
    const rewritten = [`text-decoration: ${decoration}`];
    const normalized = originalValue.toLowerCase();
    if (normalized.includes('underline')) {
      rewritten.push('text-decoration-color: var(--ct-w-inline-mark-underline-line-color, currentColor)');
    } else if (normalized.includes('line-through')) {
      rewritten.push('text-decoration-color: var(--ct-w-inline-mark-strike-line-color, currentColor)');
    }
    return rewritten;
  }

  if (name === 'text-decoration-color') {
    const tagName = options.tagName?.toLowerCase();
    if (tagName === 'u') {
      return [`text-decoration-color: ${withThemeFallback(['--ct-w-inline-mark-underline-line-color'], originalValue)}`];
    }
    if (tagName === 's') {
      return [`text-decoration-color: ${withThemeFallback(['--ct-w-inline-mark-strike-line-color'], originalValue)}`];
    }
  }

  if (options.tagName?.toLowerCase() === 'a') {
    if (name === 'text-underline-offset') {
      return [`text-underline-offset: ${withThemeFallback(['--ct-w-inline-link-underline-offset'], originalValue)}`];
    }
    if (name === 'text-decoration-thickness') {
      return [`text-decoration-thickness: ${withThemeFallback(['--ct-w-inline-link-decoration-thickness'], originalValue)}`];
    }
  }

  if (name === 'font-family') {
    const variables = isHeadingTag(options.tagName)
      ? ['--ct-w-inline-heading-font-family', '--ct-w-inline-font-family']
      : ['--ct-w-inline-font-family'];
    return [`font-family: ${withThemeFallback(variables, originalValue)}`];
  }

  if (name === 'font-weight') {
    const tagName = options.tagName?.toLowerCase();
    const variables = isHeadingTag(tagName)
      ? ['--ct-w-inline-heading-font-weight']
      : tagName === 'strong' || tagName === 'b'
        ? ['--ct-w-inline-mark-bold-weight']
        : tagName === 'a'
          ? ['--ct-w-inline-link-font-weight', '--ct-w-inline-text-font-weight']
          : ['--ct-w-inline-text-font-weight'];
    return [`font-weight: ${withThemeFallback(variables, originalValue)}`];
  }

  if (name === 'font-size') {
    const tagName = options.tagName?.toLowerCase();
    const variables = tagName === 'sup'
      ? ['--ct-w-inline-sup-font-size']
      : tagName === 'sub'
        ? ['--ct-w-inline-sub-font-size']
        : ['--ct-w-inline-font-size'];
    return [`font-size: ${withThemeFallback(variables, originalValue)}`];
  }

  const baseTypographyVariables: Record<string, string> = {
    'line-height': '--ct-w-inline-line-height',
    'letter-spacing': '--ct-w-inline-text-letter-spacing',
    'text-align': '--ct-w-inline-text-align',
  };
  if (baseTypographyVariables[name]) {
    return [`${name}: ${withThemeFallback([baseTypographyVariables[name]], originalValue)}`];
  }

  if (name === 'margin-bottom') {
    return [`margin-bottom: ${withThemeFallback(['--ct-w-inline-content-spacing'], originalValue)}`];
  }

  if ((name === 'margin-left' || name === 'padding-left') && /^-?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(originalValue)) {
    return [`${name}: calc(${originalValue} * var(--ct-w-inline-indent-scale, 1))`];
  }

  return [`${name}: ${originalValue}`];
}

function isHeadingTag(tagName?: string): boolean {
  return /^h[1-6]$/i.test(tagName ?? '');
}

function withThemeFallback(variables: string[], fallback: string): string {
  return variables.reduceRight((value, variable) => `var(${variable}, ${value})`, fallback);
}

export function reportInlineThemeIssues(issues: ClicktermInlineStyleIssue[]): void {
  if (!issues.length) {
    return;
  }
  const details = issues.map(issue => (
    `- ${issue.path}: ${formatIssueValue(issue.value)} (${issue.reason} — ${reasonHints[issue.reason]})`
  ));
  console.warn(`[Clickterm] Ignored inline theme values:\n${details.join('\n')}`);
}

function formatIssueValue(value: unknown): string {
  try {
    const serialized = JSON.stringify(value);
    return serialized === undefined ? String(value) : serialized;
  } catch (_) {
    return String(value);
  }
}
